import { Button } from '@/design-system/button';
import { FileMetadata, formatBytes, useFileUpload, type FileWithPreview } from '@/hooks/use-file-upload';
import { Translation } from '@/hoslog-intl';
import CameraIcon from '@/icons/camera-icon';
import clsx from 'clsx';
import { AlertCircleIcon, ImageIcon, UploadIcon, UserIcon, XIcon } from 'lucide-react';
import * as React from 'react';

type FileUploadContextType = {
  files: FileWithPreview[];
  isDragging: boolean;
  errors: string[];
  variant: 'rect' | 'circle' | 'circle-button' | 'document' | 'imageWithDescription';
  maxSizeMB: number;
  accept: string;
  openFileDialog: () => void;
  removeFile: (id: string) => void;
  getInputProps: (
    props?: React.InputHTMLAttributes<HTMLInputElement>,
  ) => React.InputHTMLAttributes<HTMLInputElement> & {
    ref: React.RefObject<HTMLInputElement>;
  };
  handleDragEnter: (e: React.DragEvent<HTMLElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLElement>) => void;
  handleDragOver: (e: React.DragEvent<HTMLElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLElement>) => void;
};

const FileUploadContext = React.createContext<FileUploadContextType | undefined>(undefined);

const useFileUploadContext = () => {
  const context = React.useContext(FileUploadContext);
  if (!context) {
    // @ts-expect-error – suppressing context type error
    throw new Error('FileUpload compound components cannot be rendered outside the FileUpload component');
  }
  return context;
};

// Root component
interface FileUploadProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'rect' | 'circle' | 'circle-button' | 'document' | 'imageWithDescription';
  maxSizeMB?: number;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  onFilesChange?: (files: FileWithPreview[]) => void;
  onFilesAdded?: (addedFiles: FileWithPreview[]) => void;
  initialFiles?: FileMetadata[];
  children?: React.ReactNode;
}

const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      variant = 'rect',
      maxSizeMB = 45,
      accept = 'image/svg+xml,image/png,image/jpeg,image/jpg,image/gif',
      multiple = false,
      maxFiles,
      onFilesChange,
      onFilesAdded,
      initialFiles = [],
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const maxSize = maxSizeMB * 1024 * 1024;

    const [
      { files, isDragging, errors },
      { openFileDialog, removeFile, handleDragEnter, handleDragLeave, handleDragOver, handleDrop, getInputProps },
    ] = useFileUpload({
      maxSize,
      accept,
      multiple,
      maxFiles,
      onFilesChange,
      onFilesAdded,
      initialFiles,
    });

    const contextValue = React.useMemo(
      () => ({
        files,
        isDragging,
        errors,
        variant,
        maxSizeMB,
        accept,
        openFileDialog,
        removeFile,
        getInputProps,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
      }),
      [
        files,
        isDragging,
        errors,
        variant,
        maxSizeMB,
        accept,
        openFileDialog,
        removeFile,
        getInputProps,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
      ],
    );

    return (
      <FileUploadContext.Provider value={contextValue}>
        <div ref={ref} className={clsx('flex flex-col  gap-2 ', className)} {...props}>
          {children}
        </div>
      </FileUploadContext.Provider>
    );
  },
);
FileUpload.displayName = 'FileUpload';

// DropZone component
interface DropZoneProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const DropZone = React.forwardRef<HTMLDivElement, DropZoneProps>(({ className, children, ...props }, ref) => {
  const { files, isDragging, openFileDialog, handleDragEnter, handleDragLeave, handleDragOver, handleDrop, variant } =
    useFileUploadContext();

  const isCircle = variant === 'circle' || variant === 'circle-button';
  const isCircleButton = variant === 'circle-button';
  const previewUrl = files[0]?.preview || null;
  const hasFile = files.length > 0;
  // Circle-button variant doesn't have dropzone functionality
  if (isCircleButton) {
    return (
      <div
        ref={ref}
        className={clsx(
          'relative flex items-center justify-center',
          'size-[164px] rounded-full bg-surface-subtle',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={clsx(
        'border-input transition-colors relative border border-dashed',
        'has-[input:focus]:border-ring has-[input:focus]:ring-ring/50 has-[input:focus]:ring-[3px]',
        {
          'size-[164px] rounded-full bg-surface-subtle flex items-center justify-center': isCircle,
          'w-full  rounded p-4': !isCircle && !previewUrl,
          'data-[dragging=true]:bg-accent/50': true,
        },
        className,
      )}
      role="button"
      tabIndex={0}
      onClick={hasFile ? undefined : openFileDialog}
      onKeyDown={
        hasFile
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openFileDialog();
              }
            }
      }
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      data-dragging={isDragging || undefined}
      aria-label={previewUrl ? 'Change image' : 'Upload image'}
      {...props}
    >
      {children}
    </div>
  );
});
DropZone.displayName = 'FileUpload.DropZone';

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { className?: string }>(
  ({ className, ...props }) => {
    const { getInputProps } = useFileUploadContext();

    const inputProps = getInputProps(props);

    return (
      <input
        {...inputProps}
        className={clsx('sr-only', className)}
        aria-label="Upload image file"
        ref={inputProps.ref}
      />
    );
  },
);
Input.displayName = 'FileUpload.Input';

// Preview component
interface PreviewProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallback?: React.ReactNode;
}

const Preview = React.forwardRef<HTMLImageElement, PreviewProps>(({ className, fallback, ...props }, ref) => {
  const { files, variant } = useFileUploadContext();
  const isCircle = variant === 'circle' || variant === 'circle-button';
  const isImageWithDescription = variant === 'imageWithDescription';
  const previewUrl = files[0]?.preview;

  if (!previewUrl) {
    return <div className="flex h-full items-center justify-center">{fallback}</div>;
  }

  const altText =
    files[0]?.file instanceof File
      ? files[0].file.name
      : files[0]?.file && 'name' in files[0].file
        ? files[0].file.name
        : 'Uploaded image';

  const fileName =
    files[0]?.file instanceof File
      ? files[0].file.name
      : files[0]?.file && 'name' in files[0].file
        ? files[0].file.name
        : 'Uploaded file';

  const fileSize =
    files[0]?.file instanceof File
      ? files[0].file.size
      : files[0]?.file && 'size' in files[0].file
        ? files[0].file.size
        : 0;

  if (isImageWithDescription) {
    return (
      <div className="flex items-center gap-3 rounded-md px-4 py-4.5">
        <img
          ref={ref}
          src={previewUrl}
          alt={altText}
          className={clsx('h-16 w-16 shrink-0 rounded-sm border border-border-subtle object-cover', className)}
          {...props}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground-heading">{fileName}</p>
          <p className="mt-1 text-xs text-muted-foreground">{formatBytes(fileSize)}</p>
        </div>
      </div>
    );
  }

  return (
    <img
      ref={ref}
      src={previewUrl}
      alt={altText}
      className={clsx(
        'object-cover',
        {
          'rounded-full w-full h-full': isCircle,
          'max-h-full rounded object-contain': !isCircle,
        },
        className,
      )}
      {...props}
    />
  );
});
Preview.displayName = 'FileUpload.Preview';

const RemoveButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string }
>(({ className = '', ...props }, ref) => {
  const { files, removeFile, variant } = useFileUploadContext();
  const isCircle = variant === 'circle';
  const isCircleButton = variant === 'circle-button';

  if (!files.length || isCircleButton) return null;

  return (
    <button
      ref={ref}
      type="button"
      onClick={(e) => {
        e.stopPropagation(); // Prevent triggering the dropzone click
        removeFile(files[0]?.id);
      }}
      className={clsx(
        'absolute z-10 rounded-full bg-destructive  flex justify-center items-center text-on-color  focus-visible:ring-[3px]',
        {
          '-top-3 -right-3  size-5.5': !isCircle,
          // 'top-0 right-6 size-6 border-2 border-background shadow-none': isCircle,
        },
        className,
      )}
      aria-label="Remove image"
      {...props}
    >
      {isCircle ? (
        <CameraIcon className={`rounded-full cursor-pointer text-on-color absolute left-7 top-9`} />
      ) : (
        <XIcon className={'size-4'} />
      )}
    </button>
  );
});
RemoveButton.displayName = 'FileUpload.RemoveButton';

// Circle button variant actions
const CircleButtonActions = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { files, openFileDialog, removeFile } = useFileUploadContext();
    const hasFile = files.length > 0;

    return (
      <div ref={ref} className={clsx('flex gap-2 mt-4', className)} {...props}>
        <Button variant="outline" size="sm" onClick={hasFile ? undefined : openFileDialog} className="font-medium">
          {hasFile ? '変更' : '追加'}
        </Button>
        {hasFile && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => removeFile(files[0]?.id)}
            className="text-destructive-text border-destructive/20 hover:bg-destructive/10 font-medium"
          >
            削除
          </Button>
        )}
      </div>
    );
  },
);
CircleButtonActions.displayName = 'FileUpload.CircleButtonActions';

// Placeholder component
interface PlaceholderProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const Placeholder = React.forwardRef<HTMLDivElement, PlaceholderProps>(({ className, children, ...props }, ref) => {
  const { variant, maxSizeMB } = useFileUploadContext();
  const isCircle = variant === 'circle';
  const isCircleButton = variant === 'circle-button';

  return (
    <div
      ref={ref}
      className={clsx('flex flex-col items-center justify-center text-center text-sm text-foreground', className)}
      {...props}
    >
      {children ? (
        children
      ) : isCircleButton ? (
        <UserIcon className="size-16 opacity-30" />
      ) : isCircle ? (
        <>
          <p className="font-bold">
            <Translation id="common.form.placeholder.addImage" />
          </p>
          <p className="text-xs">
            <Translation id="common.form.placeholder.dragAndDrop" />
          </p>
          <CameraIcon className={`rounded-full cursor-pointer text-on-color absolute right-0.5 top-29`} />
        </>
      ) : (
        <>
          <div className="bg-background mb-2 flex size-11 items-center justify-center rounded-full border">
            <ImageIcon className="size-4 opacity-60" />
          </div>
          <p className="mb-1.5 font-medium">Drop your image here</p>
          <p className="text-muted-foreground text-xs">SVG, PNG, JPG or GIF (max. {maxSizeMB}MB)</p>
          <Button variant="outline" className="mt-4">
            <UploadIcon className="-ms-1 size-4 opacity-60" />
            Select image
          </Button>
        </>
      )}
    </div>
  );
});
Placeholder.displayName = 'FileUpload.Placeholder';

const ProfileLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('text-foreground text-sm leading-4 select-none font-bold self-start text-left', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);
ProfileLabel.displayName = 'FileUpload.ProfileLabel';

const FileRequirement = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('text-xs text-muted-foreground mt-2', className)} {...props}>
        5MB未満のJPG、PNG、JPEGを受け付けます。
      </div>
    );
  },
);

FileRequirement.displayName = 'FileUpload.FileRequirement';

const Error = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
  const { errors } = useFileUploadContext();

  if (!errors.length) return null;

  return (
    <div
      ref={ref}
      className={clsx('text-destructive-text flex items-center gap-1 text-xs mt-1', className)}
      role="alert"
      {...props}
    >
      <AlertCircleIcon className="size-3 shrink-0" />
      <span>{errors[0]}</span>
    </div>
  );
});
Error.displayName = 'FileUpload.Error';

const Footer = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }, ref) => {
    const { variant } = useFileUploadContext();
    const isCircle = variant === 'circle';
    const isCircleButton = variant === 'circle-button';

    if (isCircle || isCircleButton) return null;

    return (
      <p
        ref={ref}
        aria-live="polite"
        role="region"
        className={clsx('text-muted-foreground mt-2 text-center text-xs', className)}
        {...props}
      >
        {children || <>Single image uploader w/ max size (drop area + button) ∙ </>}
      </p>
    );
  },
);
Footer.displayName = 'FileUpload.Footer';

// Multi file preview component (for future use with multiple file upload)
interface FilePreviewListProps extends React.HTMLAttributes<HTMLUListElement> {
  renderItem?: (file: FileWithPreview, index: number) => React.ReactNode;
}

const FilePreviewList = React.forwardRef<HTMLUListElement, FilePreviewListProps>(
  ({ className, renderItem, ...props }, ref) => {
    const { files, removeFile } = useFileUploadContext();

    if (!files.length) return null;

    return (
      <ul ref={ref} className={clsx('w-full space-y-2 mt-2', className)} {...props}>
        {files.map((file, index) => {
          const fileName =
            file.file instanceof File ? file.file.name : 'name' in file.file ? file.file.name : 'Unnamed file';

          return (
            <li key={file.id} className="flex items-center justify-between rounded-md border p-2">
              {renderItem ? (
                renderItem(file, index)
              ) : (
                <>
                  <div className="flex items-center gap-2 truncate">
                    <div className="flex  shrink-0 items-center justify-center rounded-full bg-muted">
                      <ImageIcon className=" opacity-60" />
                    </div>
                    <span className="truncate text-sm">{fileName}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(file.id)}
                    className="ml-2 flex size-6 shrink-0 items-center justify-center rounded-full hover:bg-muted"
                    aria-label={`Remove ${fileName}`}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
    );
  },
);
FilePreviewList.displayName = 'FileUpload.FilePreviewList';

type FileUploadComponent = typeof FileUpload & {
  DropZone: typeof DropZone;
  Input: typeof Input;
  Preview: typeof Preview;
  RemoveButton: typeof RemoveButton;
  Placeholder: typeof Placeholder;
  Error: typeof Error;
  Footer: typeof Footer;
  FilePreviewList: typeof FilePreviewList;
  CircleButtonActions: typeof CircleButtonActions;
  ProfileLabel: typeof ProfileLabel;
  FileRequirement: typeof FileRequirement;
};

(FileUpload as FileUploadComponent).DropZone = DropZone;
(FileUpload as FileUploadComponent).Input = Input;
(FileUpload as FileUploadComponent).Preview = Preview;
(FileUpload as FileUploadComponent).RemoveButton = RemoveButton;
(FileUpload as FileUploadComponent).Placeholder = Placeholder;
(FileUpload as FileUploadComponent).Error = Error;
(FileUpload as FileUploadComponent).Footer = Footer;
(FileUpload as FileUploadComponent).FilePreviewList = FilePreviewList;
(FileUpload as FileUploadComponent).CircleButtonActions = CircleButtonActions;
(FileUpload as FileUploadComponent).ProfileLabel = ProfileLabel;
(FileUpload as FileUploadComponent).FileRequirement = FileRequirement;

export const FileUploadWithCompound = FileUpload as FileUploadComponent;
export { FileUploadWithCompound as FileUpload };
